"use client";

import { useMemo, useState } from "react";
import { Pill } from "@/components/Icons";
import { useStock } from "@/context/StockContext";
import { euro } from "@/lib/utils";

const STATUT_LIB: Record<string, string> = {
  to_be_processed: "À traiter",
  late: "En retard",
  paid: "Payée",
  partially_paid: "Partiellement payée",
};

export function FournisseursView() {
  const { achats, syncing, ocrAchat, showToast } = useStock();
  const [q, setQ] = useState("");
  const [ocrEnCours, setOcrEnCours] = useState<Record<string, boolean>>({});

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return achats;
    return achats.filter((a) => {
      const hay = `${a.num} ${a.fournisseur} ${a.lignes.map((l) => l.texte).join(" ")}`.toLowerCase();
      return hay.includes(query);
    });
  }, [achats, q]);

  const ttc = achats.reduce((n, a) => n + a.montantTtc, 0);
  const fournisseurs = new Set(achats.map((a) => a.fournisseur)).size;
  const sansDetail = achats.filter((a) => a.detailManquant).length;

  const lancerOcr = async (id: string) => {
    if (ocrEnCours[id]) return;
    setOcrEnCours((s) => ({ ...s, [id]: true }));
    try {
      await ocrAchat(id);
    } catch (error) {
      showToast(error instanceof Error ? error.message : "OCR impossible");
    } finally {
      setOcrEnCours((s) => ({ ...s, [id]: false }));
    }
  };

  return (
    <>
      <p className="muted" style={{ maxWidth: "68ch", marginBottom: 18 }}>
        Factures fournisseurs Pennylane depuis février. Si le détail manque, ouvre la facture : le
        PDF est lu par OCR automatiquement.
      </p>
      <div className="kpis" style={{ marginBottom: 18 }}>
        <div className="kpi">
          <span className="eyebrow">Achats</span>
          <b>{achats.length}</b>
          <span className="muted">depuis février</span>
        </div>
        <div className="kpi">
          <span className="eyebrow">Fournisseurs</span>
          <b>{fournisseurs}</b>
          <span className="muted">distincts</span>
        </div>
        <div className={`kpi ${sansDetail ? "alert" : ""}`}>
          <span className="eyebrow">Sans détail</span>
          <b>{sansDetail}</b>
          <span className="muted">OCR au clic</span>
        </div>
        <div className="kpi">
          <span className="eyebrow">Total TTC</span>
          <b>{euro(ttc)}</b>
          <span className="muted">période importée</span>
        </div>
      </div>

      <div className="toolbar" style={{ marginBottom: 12 }}>
        <div className="search">
          <input
            type="search"
            placeholder="Fournisseur, n° facture, libellé…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      {!achats.length && (
        <p className="muted">
          {syncing ? "Lecture des achats Pennylane…" : "Aucun achat importé. Lance Synchroniser."}
        </p>
      )}

      <div className="inv">
        {filtered.map((a) => (
          <details
            key={a.id}
            onToggle={(e) => {
              const open = (e.currentTarget as HTMLDetailsElement).open;
              if (open && a.detailManquant && !a.ocrFait && !ocrEnCours[a.id]) {
                void lancerOcr(a.id);
              }
            }}
          >
            <summary>
              <span>
                <b className="mono">{a.num}</b>{" "}
                <span className="muted" style={{ fontSize: ".86rem" }}>
                  · {a.quand} · {a.fournisseur}
                </span>
              </span>
              <span className="mono">{euro(a.montantTtc)}</span>
              {a.detailManquant ? (
                <Pill kind="warn">{ocrEnCours[a.id] ? "OCR…" : "Sans détail"}</Pill>
              ) : (
                <Pill kind={a.paymentStatus === "paid" ? "ok" : "warn"}>
                  {STATUT_LIB[a.paymentStatus ?? ""] ?? a.paymentStatus ?? "Achat"}
                </Pill>
              )}
            </summary>
            <ul className="lines list">
              {ocrEnCours[a.id] && (
                <li>
                  <span className="muted">Lecture OCR du PDF en cours…</span>
                </li>
              )}
              {a.lignes.map((l) => (
                <li key={l.id}>
                  <span>{l.texte}</span>
                  <span className="delta zero">×{l.qte}</span>
                  <span className="mono muted">{euro(l.montant)}</span>
                </li>
              ))}
              <li>
                <span className="muted">
                  {a.fichier ? a.fichier : "Document Pennylane"}
                  {a.detailManquant ? " · détail à lire dans le PDF" : ""}
                </span>
                <span style={{ display: "inline-flex", gap: 6 }}>
                  <button
                    className={`btn tiny ${a.detailManquant ? "primary" : ""}`}
                    type="button"
                    disabled={Boolean(ocrEnCours[a.id])}
                    onClick={() => void lancerOcr(a.id)}
                  >
                    {ocrEnCours[a.id] ? "OCR…" : a.detailManquant ? "Lire le PDF" : "Relire le PDF"}
                  </button>
                  <a
                    className="btn tiny"
                    href={`/api/pennylane/achats/pdf?id=${encodeURIComponent(a.id)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Voir le PDF
                  </a>
                </span>
              </li>
            </ul>
          </details>
        ))}
      </div>
    </>
  );
}
